import { useEffect, useState, useCallback, useMemo } from "react";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { ShieldAlert, CheckCircle2, Edit3, EyeOff, ExternalLink, ListChecks, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { formatEur, formatNumber, formatDateTime } from "../lib/format";
import { toast } from "sonner";

const DataQuality = () => {
    const [summary, setSummary] = useState(null);
    const [activeFlag, setActiveFlag] = useState(null); // null = ALL flagged
    const [issues, setIssues] = useState([]);
    const [audit, setAudit] = useState([]);
    const [loading, setLoading] = useState(true);
    const [backfilling, setBackfilling] = useState(false);
    const [editing, setEditing] = useState(null); // { listing }

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, i, a] = await Promise.all([
                api.get("/quality/summary"),
                api.get(`/quality/issues${activeFlag ? `?flag=${activeFlag}` : ""}`),
                api.get("/quality/audit-log?limit=30"),
            ]);
            setSummary(s.data);
            setIssues(i.data);
            setAudit(a.data);
        } catch (e) {
            toast.error("Failed to load data quality", { description: String(e) });
        } finally {
            setLoading(false);
        }
    }, [activeFlag]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const runBackfill = async () => {
        setBackfilling(true);
        try {
            const { data } = await api.post("/quality/backfill");
            toast.success("Backfill complete", { description: `${data.updated} listing(s) reflagged` });
            await loadAll();
        } catch (e) {
            toast.error("Backfill failed", { description: String(e) });
        } finally {
            setBackfilling(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="quality-page">
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-serif flex items-center gap-3">
                        <ShieldAlert className="w-7 h-7 text-amber-400" /> Data Quality Center
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Suspicious values are flagged automatically and excluded from market
                        averages by default. Review each issue, then confirm, correct, or
                        exclude. Every action is audit-logged.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={runBackfill} disabled={backfilling} data-testid="run-backfill-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${backfilling ? "animate-spin" : ""}`} />
                    Re-evaluate all listings
                </Button>
            </header>

            {loading || !summary ? (
                <Skeleton className="h-24" />
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat label="Total listings" value={formatNumber(summary.total_listings)} />
                    <Stat label="Flagged" value={formatNumber(summary.flagged_listings)} variant={summary.flagged_listings > 0 ? "warn" : "ok"} />
                    <Stat label="Excluded from market stats" value={formatNumber(summary.excluded_from_market_stats)} variant="warn" />
                    <Stat label="Human-reviewed" value={formatNumber(summary.human_reviewed)} variant={summary.human_reviewed > 0 ? "ok" : "muted"} />
                </div>
            )}

            {/* Flag pills */}
            {summary && (
                <Card className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Filter by flag</div>
                    <div className="flex flex-wrap gap-2" data-testid="flag-filter-bar">
                        <FlagPill active={activeFlag === null} onClick={() => setActiveFlag(null)} label={`All flagged (${summary.flagged_listings})`} />
                        {Object.entries(summary.per_flag)
                            .filter(([k]) => k !== "NEEDS_HUMAN_VERIFICATION")
                            .map(([code, count]) => (
                                <FlagPill
                                    key={code}
                                    active={activeFlag === code}
                                    onClick={() => setActiveFlag(code)}
                                    label={summary.flag_labels?.[code] || code}
                                    count={count}
                                    code={code}
                                />
                            ))}
                    </div>
                </Card>
            )}

            <Card className="p-0 overflow-hidden">
                <div className="px-5 py-3 border-b border-border/60 flex items-center justify-between">
                    <h2 className="text-lg font-serif flex items-center gap-2">
                        <ListChecks className="w-4 h-4 text-accent" />
                        Issues
                        <span className="text-sm text-muted-foreground font-sans">· {issues.length}</span>
                    </h2>
                </div>
                {issues.length === 0 ? (
                    <div className="p-10 text-center text-sm text-muted-foreground" data-testid="quality-empty">
                        No listings carry the selected flag. Real data only — nothing fabricated.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">
                                    <Th>Title</Th>
                                    <Th>Source</Th>
                                    <Th right>Price</Th>
                                    <Th right>m²</Th>
                                    <Th right>€/m²</Th>
                                    <Th>Location</Th>
                                    <Th>Flags</Th>
                                    <Th>Actions</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {issues.map((l) => (
                                    <tr key={l.id} className="border-b border-border/30 hover:bg-muted/30" data-testid={`issue-row-${l.id}`}>
                                        <Td>
                                            <div className="font-medium line-clamp-1 max-w-[260px]">{l.title || <span className="italic text-muted-foreground">Untitled</span>}</div>
                                            {l.original_url && (
                                                <a href={l.original_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-muted-foreground hover:text-accent flex items-center gap-1">
                                                    open <ExternalLink className="w-3 h-3" />
                                                </a>
                                            )}
                                        </Td>
                                        <Td className="text-[11px]">{l.provenance?.source_name || "—"}</Td>
                                        <Td right>{l.asking_price_eur != null ? formatEur(l.asking_price_eur) : <NA />}</Td>
                                        <Td right>{l.living_area_sqm != null ? `${formatNumber(l.living_area_sqm, 0)}` : <NA />}</Td>
                                        <Td right>{l.price_per_sqm_eur != null ? `€${formatNumber(l.price_per_sqm_eur, 0)}` : <NA />}</Td>
                                        <Td className="text-[11px]">{[l.municipality, l.neighbourhood].filter(Boolean).join(" · ") || <NA />}</Td>
                                        <Td>
                                            <div className="flex flex-wrap gap-1">
                                                {(l.data_quality_flags || []).filter((f) => f !== "NEEDS_HUMAN_VERIFICATION").map((f) => (
                                                    <Badge key={f} variant="outline" className="text-[9px] border-amber-500/30 text-amber-400">{f}</Badge>
                                                ))}
                                            </div>
                                        </Td>
                                        <Td>
                                            <Button size="sm" variant="outline" onClick={() => setEditing(l)} data-testid={`review-btn-${l.id}`}>
                                                <Edit3 className="w-3.5 h-3.5 mr-1" /> Review
                                            </Button>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Card className="p-5" data-testid="audit-log-panel">
                <h2 className="text-lg font-serif mb-3">Correction audit log</h2>
                {audit.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No corrections recorded yet.</p>
                ) : (
                    <div className="space-y-2 text-[12px]">
                        {audit.map((row) => (
                            <div key={row.id} className="border border-border/40 rounded p-3 bg-muted/20" data-testid={`audit-row-${row.id}`}>
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="text-foreground/80">
                                        <span className="font-medium">{row.corrected_by}</span> · listing <code className="text-[10px]">{row.listing_id.slice(0, 8)}…</code>
                                    </span>
                                    <span className="text-muted-foreground">{formatDateTime(row.corrected_at)}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-[11px]">
                                    <div>
                                        <span className="text-muted-foreground">Old:</span>{" "}
                                        <code>{JSON.stringify(row.old_values)}</code>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">New:</span>{" "}
                                        <code>{JSON.stringify(row.new_values)}</code>
                                    </div>
                                </div>
                                {row.reason && (
                                    <div className="text-muted-foreground mt-1 italic">"{row.reason}"</div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {editing && (
                <ReviewDialog
                    listing={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await loadAll();
                    }}
                />
            )}
        </div>
    );
};

const FlagPill = ({ active, onClick, label, count, code }) => (
    <button
        onClick={onClick}
        className={`px-3 py-1 rounded-full text-[11px] border transition-colors ${
            active
                ? "bg-accent/20 border-accent/50 text-accent"
                : "border-border/60 text-muted-foreground hover:bg-muted/50"
        }`}
        data-testid={`flag-filter-${code || "all"}`}
    >
        {label}
        {typeof count === "number" && (
            <span className="ml-1.5 text-[9px] opacity-70">({count})</span>
        )}
    </button>
);

const Stat = ({ label, value, variant }) => (
    <Card className={`p-4 ${variant === "warn" ? "border-amber-500/30" : variant === "ok" ? "border-emerald-500/30" : ""}`}>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-2xl font-serif mt-2">{value}</div>
    </Card>
);

const Th = ({ children, right }) => (
    <th className={`py-2 px-3 font-medium ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, className = "" }) => (
    <td className={`py-2 px-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>
);
const NA = () => <span className="text-muted-foreground italic">N/A</span>;

const ACTION_OPTIONS = [
    { value: "confirm", label: "Confirm value (mark as verified)" },
    { value: "correct", label: "Correct values manually" },
    { value: "exclude", label: "Exclude from market stats" },
    { value: "keep", label: "Keep flagged (no change)" },
];

const ReviewDialog = ({ listing, onClose, onSaved }) => {
    const [action, setAction] = useState("confirm");
    const [reason, setReason] = useState("");
    const [price, setPrice] = useState(listing.asking_price_eur ?? "");
    const [area, setArea] = useState(listing.living_area_sqm ?? "");
    const [muni, setMuni] = useState(listing.municipality ?? "");
    const [neigh, setNeigh] = useState(listing.neighbourhood ?? "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!reason.trim()) {
            toast.error("A reason is required for every correction");
            return;
        }
        setSaving(true);
        try {
            const payload = { reason, corrected_by: "operator" };
            if (action === "confirm") {
                payload.quality_reviewed = true;
                payload.excluded_from_market_stats = false;
            } else if (action === "exclude") {
                payload.quality_reviewed = true;
                payload.excluded_from_market_stats = true;
            } else if (action === "correct") {
                payload.quality_reviewed = true;
                if (price !== "" && Number(price) !== listing.asking_price_eur) payload.asking_price_eur = Number(price);
                if (area !== "" && Number(area) !== listing.living_area_sqm) payload.living_area_sqm = Number(area);
                if (muni !== listing.municipality) payload.municipality = muni;
                if (neigh !== listing.neighbourhood) payload.neighbourhood = neigh;
            } else {
                payload.quality_reviewed = true;
            }
            const { data } = await api.post(`/listings/${listing.id}/correct`, payload);
            toast.success(data.changed ? "Correction saved" : "Confirmed", {
                description: data.changed ? `Audit logged · flags now: ${data.listing.data_quality_flags?.join(", ") || "—"}` : "No field changed.",
            });
            await onSaved();
        } catch (e) {
            toast.error("Correction failed", { description: e?.response?.data?.detail || String(e) });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-xl" data-testid="review-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-xl">Review listing</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="text-foreground/80 line-clamp-2">{listing.title}</div>
                    <div className="text-[11px] text-muted-foreground">
                        {listing.provenance?.source_name && <span>via {listing.provenance.source_name} · </span>}
                        <a href={listing.original_url} target="_blank" rel="noopener noreferrer" className="hover:text-accent underline">open source</a>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {(listing.data_quality_flags || []).map((f) => (
                            <Badge key={f} variant="outline" className="text-[9px] border-amber-500/30 text-amber-400">{f}</Badge>
                        ))}
                    </div>

                    <div className="pt-1">
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Action</label>
                        <Select value={action} onValueChange={setAction}>
                            <SelectTrigger className="mt-1" data-testid="review-action-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {ACTION_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {action === "correct" && (
                        <div className="grid grid-cols-2 gap-3 pt-2">
                            <Field label="Asking price (EUR)">
                                <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="review-price-input" />
                            </Field>
                            <Field label="Area (m²)">
                                <Input type="number" value={area} onChange={(e) => setArea(e.target.value)} data-testid="review-area-input" />
                            </Field>
                            <Field label="Municipality">
                                <Input value={muni} onChange={(e) => setMuni(e.target.value)} data-testid="review-muni-input" />
                            </Field>
                            <Field label="Neighbourhood">
                                <Input value={neigh} onChange={(e) => setNeigh(e.target.value)} data-testid="review-neigh-input" />
                            </Field>
                        </div>
                    )}

                    <Field label="Reason (required)">
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            placeholder="e.g. Cross-checked with broker, price is correct."
                            data-testid="review-reason-input"
                        />
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} disabled={saving || !reason.trim()} data-testid="review-save-btn">
                        {action === "confirm" ? <CheckCircle2 className="w-4 h-4 mr-1.5" /> : action === "exclude" ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Edit3 className="w-4 h-4 mr-1.5" />}
                        {saving ? "Saving…" : "Save & audit-log"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-1">{label}</label>
        {children}
    </div>
);

export default DataQuality;
